import Duck from 'extensible-duck';
import normalize from './normalize';
import get from 'lodash/get';
import merge from 'lodash/merge';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import qs from 'qs';

// import produce from 'appDir/lib/functions/produce';
import produce from 'immer';
import requestsManagerAxios from '../../lib/requestsManagerAxios';
import handleJsonApiErrorReducer from '../../lib/functions/handleJsonApiErrorReducer';

const transformUrlParams = (params = {}) => {
  let transformedParams = {};
  Object.keys(params).forEach(key => {
    if (isObject(params[key])) {
      Object.keys(params[key]).forEach(innerKey => {
        transformedParams[`${key}[${innerKey}]`] = params[key][innerKey];
      });
    } else {
      transformedParams[key] = params[key];
    }
  });
  transformedParams = qs.stringify(params, {
    encode: false,
    arrayFormat: 'comma',
    addQueryPrefix: true,
  });
  return transformedParams;
};

export default new Duck({
  namespace: 'app',
  store: 'jsonApi',
  consts: {
    namespaces: [
      'ADMIN_BACKGROUND_CHECKS_INDEX',
      'ADMIN_CHECKR_CANDIDATE_SHOW',
      'ADMIN_SETTINGS_ADMINS',
      'CURRENT_USER',
      'CURRENT_ORGANIZATION',
      'JWADMIN_ORGANIZATION_EDIT',
      'JWADMIN_ORGANIZATIONS_INDEX',
    ],
  },
  types: [
    'CREATE_RESOURCE',
    'CREATE_RESOURCE_PENDING',
    'CREATE_RESOURCE_REJECTED',
    'CREATE_RESOURCE_FULFILLED',
    'DELETE_RESOURCE',
    'DELETE_RESOURCE_PENDING',
    'DELETE_RESOURCE_REJECTED',
    'DELETE_RESOURCE_FULFILLED',
    'INITIALIZE_NAMESPACE',
    'INITIALIZE_STATE',
    'READ_ENDPOINT',
    'READ_ENDPOINT_PENDING',
    'READ_ENDPOINT_REJECTED',
    'READ_ENDPOINT_FULFILLED',
    'UPDATE_RESOURCE',
    'UPDATE_RESOURCE_PENDING',
    'UPDATE_RESOURCE_REJECTED',
    'UPDATE_RESOURCE_FULFILLED',
  ],
  initialState: {
    isCreating: 0,
    isDeleting: 0,
    isReading: 0,
    isUpdating: 0,
  },
  reducer: produce(
    (draft, { type, payload, meta }, { types, initialState }) => {
      switch (type) {
        case types.CREATE_RESOURCE_PENDING:
          draft.isCreating = draft.isCreating + 1;
          return;
        case types.CREATE_RESOURCE_REJECTED:
          handleJsonApiErrorReducer(payload);
          draft.isCreating = draft.isCreating - 1;
          return;
        case types.CREATE_RESOURCE_FULFILLED: {
          const { result, ...resources } = normalize(payload.data);
          draft.isCreating = draft.isCreating - 1;
          draft = merge(draft, resources);
          if (meta.onFulfilled) meta.onFulfilled(draft, payload);
          return;
        }
        case types.DELETE_RESOURCE_PENDING:
          draft.isDeleting = draft.isDeleting + 1;
          return;
        case types.DELETE_RESOURCE_REJECTED:
          handleJsonApiErrorReducer(payload);
          draft.isDeleting = draft.isDeleting - 1;
          return;
        case types.DELETE_RESOURCE_FULFILLED:
          draft.isDeleting = draft.isDeleting - 1;
          delete draft[meta.type][meta.id];
          return;
        case types.INITIALIZE_NAMESPACE:
          delete draft[payload.namespace];
          return;
        case types.INITIALIZE_STATE:
          return initialState;
        case types.READ_ENDPOINT_PENDING:
          draft.isReading = draft.isReading + 1;
          return draft;
        case types.READ_ENDPOINT_REJECTED:
          handleJsonApiErrorReducer(payload, { toast404: meta.toast404 });
          draft.isReading--;
          return;
        case types.READ_ENDPOINT_FULFILLED: {
          const { result, ...resources } = normalize(payload.data);

          if (meta.initializeState) {
            return merge(Object.assign({}, initialState), {
              ...resources,
              [meta.namespace]: { result },
            });
          }

          draft.isReading--;
          if (meta.initializeNamespace) draft[meta.namespace] = {};
          // if (meta.initializeState) Object.assign(draft, initialState);

          // merge 'types'. this will make it easy to figure out which types we are modifying so reselect can pay attention to them.
          // normalized.meta[meta.endpoint].types = [];
          // let { types } = normalized.meta[meta.endpoint];
          // types.push(get(payload.data, ['data', 0, 'type']));
          // (get(payload.data, ['included']) || []).forEach(included =>
          //   types.push(included.type)
          // );
          // types = uniqWith(types, (a, b) => a === b).filter(
          //   type => typeof type === 'string'
          // );
          // normalized.meta[meta.endpoint].types = types;

          merge(draft, {
            ...resources,
            [meta.namespace]: { result },
          });
          return;
        }
        case types.UPDATE_RESOURCE_PENDING:
          draft.isUpdating = draft.isUpdating + 1;
          return;
        case types.UPDATE_RESOURCE_REJECTED:
          handleJsonApiErrorReducer(payload);
          draft.isUpdating = draft.isUpdating - 1;
          return;
        case types.UPDATE_RESOURCE_FULFILLED: {
          const { result, ...resources } = normalize(payload.data);
          draft.isUpdating = draft.isUpdating - 1;

          draft = merge(draft, resources);
          return;
        }
      }
    }
  ),
  selectors: {
    getMeta: (store, namespace) =>
      get(store.jsonApiStore, [namespace, 'result', 'meta']) || {},
    getNestedResourceData: (store, namespace, options = {}) => {
      const { namespaceResources = false } = options;
      const fetchFromNormalizedData = resourceIdentifierObject => {
        const path = namespaceResources
          ? [
              namespace,
              resourceIdentifierObject.type,
              resourceIdentifierObject.id,
            ]
          : [resourceIdentifierObject.type, resourceIdentifierObject.id];
        const resource = get(store.jsonApiStore, path); // fetch the actual resource via the reference
        if (!resource) return; // if nothing exists, go to the next iteration in the loop

        const tempObject = {
          id: resource.id,
          type: resource.type,
          attributes: resource.attributes,
        }; // create a temp object that includes the id, type, and attributes from the actual resource
        if (resource.relationships) {
          // if relationships exist
          tempObject.relationships = {}; // create the relationships key on the temp object
          Object.keys(resource.relationships).forEach(key => {
            // loop over the relationship keys in the reference
            const { data: relationshipData } = resource.relationships[key]; // get the data object/array from the relationship via the reference
            tempObject.relationships[key] = {
              data: fetchWithResourceIdentifierData(relationshipData),
            }; // recursivively fetch the data for this relationship and attach it to the relationship on the tempObject
          });
        }
        return tempObject;
      };

      const fetchWithResourceIdentifierData = resourceIdentifierData => {
        if (isArray(resourceIdentifierData)) {
          // if the reference data is an array
          let tempArray = []; // create a temp array to store the new data
          resourceIdentifierData.forEach(resourceIdentifierObject => {
            // loop over the reference data
            const resourceObject = fetchFromNormalizedData(
              resourceIdentifierObject
            );
            if (resourceObject) tempArray.push(resourceObject);
          });
          return tempArray; // return the temp array
        } else if (isObject(resourceIdentifierData)) {
          // reference data is an object
          return fetchFromNormalizedData(resourceIdentifierData); // return the temp object
        } else {
          return null;
        }
      };
      return fetchWithResourceIdentifierData(
        get(store.jsonApiStore, [namespace, 'result', 'data'])
      );
    },
    getRelationship: (
      store,
      namespace,
      parentType,
      parentId,
      relationshipType
    ) => {
      const resourceIdentifierObject =
        get(store.jsonApiStore, [
          namespace,
          parentType,
          parentId,
          'relationships',
          relationshipType,
          'data',
        ]) || {};
      return get(store.jsonApiStore, [
        namespace,
        resourceIdentifierObject.type,
        resourceIdentifierObject.id,
      ]);
    },
    getRelationships: (
      store,
      namespace,
      parentType,
      parentId,
      relationshipType
    ) => {
      const array =
        get(store.jsonApiStore, [
          namespace,
          parentType,
          parentId,
          'relationships',
          relationshipType,
          'data',
        ]) || [];
      return array
        .map(({ id, type }) => get(store.jsonApiStore, [namespace, type, id]))
        .filter(resource => resource);
    },
    getResource: (store, namespace) => {
      const object =
        get(store.jsonApiStore, [namespace, 'result', 'data']) || {};
      const { id, type } = object;
      return get(store.jsonApiStore, [type, id]);
    },
    getResources: (store, namespace) =>
      (get(store.jsonApiStore, [namespace, 'result', 'data']) || []).map(
        ({ id, type }) => store.jsonApiStore[type][id]
      ),
    isBusy: store =>
      store.jsonApiStore.isCreating > 0 ||
      store.jsonApiStore.isDeleting > 0 ||
      store.jsonApiStore.isUpdating > 0 ||
      store.jsonApiStore.isReading > 0,
    isSubmitting: store =>
      store.jsonApiStore.isCreating > 0 ||
      store.jsonApiStore.isDeleting > 0 ||
      store.jsonApiStore.isUpdating > 0,
  },
  creators: ({ types }) => ({
    createResource: (endpoint, resource, options = {}) => ({
      type: types.CREATE_RESOURCE,
      payload: requestsManagerAxios.post(
        `${endpoint}${transformUrlParams(options.urlParams)}`,
        resource,
        {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
          },
        }
      ),
      meta: { onFulfilled: options.onFulfilled, namespace: options.namespace },
    }),
    deleteResource: (endpoint, type, id, namespace = undefined) => ({
      // JSON API COMPLIANT
      type: types.DELETE_RESOURCE,
      payload: requestsManagerAxios.delete(endpoint, {
        headers: {
          Accept: 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
        },
      }),
      meta: { endpoint, type, id, namespace },
    }),
    readEndpoint: (endpoint, namespace, options = {}) => {
      const {
        initializeNamespace = false,
        initializeState = false,
        toast404,
      } = options;
      return {
        type: types.READ_ENDPOINT,
        payload: requestsManagerAxios.get(
          `${endpoint}${transformUrlParams(options.urlParams)}`,
          {
            headers: { Accept: 'application/vnd.api+json' },
          }
        ),
        meta: {
          namespace,
          initializeNamespace,
          initializeState,
          toast404,
        },
      };
    },
    initializeNamespace: namespace => ({
      type: types.INITIALIZE_NAMESPACE,
      payload: { namespace },
    }),
    initializeState: () => ({ type: types.INITIALIZE_STATE }),
    updateResource: (endpoint, resource, options = {}) => ({
      type: types.UPDATE_RESOURCE,
      payload: requestsManagerAxios.patch(
        `${endpoint}${transformUrlParams(options.urlParams)}`,
        resource,
        {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
          },
        }
      ),
      meta: { namespace: options.namespace },
    }),
  }),
});
